import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, MessageCircle, Plus, Search, Users, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { fetchReferentiAsContacts, mergeContactsByEmail } from '../../../lib/utils/fetchReferentiContacts';
import { persistClientReferenti } from '../../../lib/utils/persistClientContacts';
import { GroupPicker } from '../../whatsapp/GroupPicker';
import { ReuseIntroChoice } from './ReuseIntroChoice';

const inputCls =
  'w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors';

interface ClientContact {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  role: string | null;
  area: string | null;
}

interface Props {
  clientId: string;
  mode: 'new' | 'existing';
  onMode: (m: 'new' | 'existing') => void;
  numbers: string[];
  onNumbersChange: (numbers: string[]) => void;
  groupName: string;
  onGroupName: (v: string) => void;
  reuseJid: string;
  onReuseJid: (v: string) => void;
  /** Riuso gruppo: mandare il messaggio di benvenuto dell'automazione? */
  sendIntro?: boolean;
  onSendIntro?: (v: boolean) => void;
  clientName?: string;
  showErrors: boolean;
}

/** Solo cifre, ultime 10 (per confronto numeri robusto a prefissi/spazi). */
const phoneKey = (p: string) => p.replace(/\D/g, '').slice(-10);

const AREA_OPTIONS = [
  { value: 'amministrazione', label: 'Amministrazione' },
  { value: 'legale_rappresentante', label: 'Legale rappresentante' },
  { value: 'commerciale', label: 'Commerciale' },
  { value: 'tecnico', label: 'Tecnico' },
  { value: 'altro', label: 'Altro' },
];

/** Step 2 del wizard: scelta tra creare un nuovo gruppo WhatsApp o riusarne uno,
 * con i numeri presi dai contatti del cliente (multi-select) o aggiunti a mano. */
export function AcceptanceStepWhatsapp({
  clientId, mode, onMode, numbers, onNumbersChange, groupName, onGroupName, reuseJid, onReuseJid,
  sendIntro = true, onSendIntro, clientName, showErrors,
}: Props) {
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  // Carica contatti del cliente + organization_id (per l'eventuale insert).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      const [{ data: cdata }, { data: cli }, refMap] = await Promise.all([
        supabase.from('client_contacts')
          .select('id, name, phone, email, role, area')
          .eq('client_id', clientId).eq('is_active', true)
          .order('is_primary', { ascending: false }),
        supabase.from('clients').select('organization_id').eq('id', clientId).maybeSingle(),
        // Modello Contatti/Aziende first-class: i referenti vivono in client_relationships
        // (client_contacts vuota per i clienti nuovi). Senza il merge la lista partecipanti
        // resta vuota e la validazione blocca il wizard di accettazione.
        fetchReferentiAsContacts([clientId]),
      ]);
      if (cancelled) return;
      const merged = mergeContactsByEmail(
        ((cdata as unknown) as Parameters<typeof mergeContactsByEmail>[0]) ?? [],
        refMap[clientId] ?? [],
      ).map((c) => ({
        id: c.id,
        name: c.name || '',
        phone: c.phone ?? null,
        email: c.email ?? null,
        role: c.role ?? null,
        area: (c.area ?? null) as string | null,
      }));
      setContacts(merged as ClientContact[]);
      setOrgId((cli as { organization_id?: string } | null)?.organization_id ?? null);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const selectedKeys = useMemo(() => new Set(numbers.map(phoneKey).filter(Boolean)), [numbers]);
  const contactKeys = useMemo(
    () => new Set(contacts.map((c) => (c.phone ? phoneKey(c.phone) : '')).filter(Boolean)),
    [contacts],
  );

  const toggleContact = (c: ClientContact) => {
    if (!c.phone) return;
    const key = phoneKey(c.phone);
    if (selectedKeys.has(key)) {
      onNumbersChange(numbers.filter((n) => phoneKey(n) !== key));
    } else {
      onNumbersChange([...numbers.filter((n) => n.trim() !== ''), c.phone]);
    }
  };

  // Numeri inseriti a mano = quelli che non corrispondono a nessun contatto.
  const manualNumbers = numbers.filter((n) => n.trim() !== '' && !contactKeys.has(phoneKey(n)));

  const withContacts = contacts.filter((c) => !!c.phone);
  const filtered = withContacts.filter((c) =>
    !search.trim() ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone ?? '').includes(search) ||
    (c.email ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  const tabCls = (active: boolean) =>
    `flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-medium rounded-md border transition-colors ${
      active
        ? 'border-[#2383E2] bg-[#2383E2]/[0.08] text-[#2383E2]'
        : 'border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] text-[#6B6762] dark:text-[#9B9A97] hover:bg-black/[0.02]'
    }`;

  const hasNumber = numbers.some((n) => n.trim() !== '');
  const numbersError = showErrors && mode === 'new' && !hasNumber;
  const nameError = showErrors && mode === 'new' && !groupName.trim();
  const pickError = showErrors && mode === 'existing' && !reuseJid;

  const onContactAdded = (c: ClientContact) => {
    setContacts((prev) => [c, ...prev]);
    if (c.phone) onNumbersChange([...numbers.filter((n) => n.trim() !== ''), c.phone]);
    setShowAdd(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <MessageCircle className="w-4 h-4 text-[#6B6762] dark:text-[#9B9A97] mt-0.5 flex-shrink-0" />
        <p className="text-[12px] text-[#6B6762] dark:text-[#9B9A97]">
          Alla firma verrà gestito un <strong>gruppo WhatsApp con il cliente</strong>.
        </p>
      </div>

      {/* Scelta modalità */}
      <div className="flex gap-2">
        <button type="button" onClick={() => onMode('new')} className={tabCls(mode === 'new')}>
          <Plus className="w-3.5 h-3.5" /> Crea un nuovo gruppo
        </button>
        <button type="button" onClick={() => onMode('existing')} className={tabCls(mode === 'existing')}>
          <Users className="w-3.5 h-3.5" /> Usa un gruppo esistente
        </button>
      </div>

      {mode === 'new' && (
        <div>
          <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">
            Nome del gruppo <span className="text-[#EB5757]">*</span>
          </label>
          <input
            type="text" value={groupName} onChange={(e) => onGroupName(e.target.value)}
            placeholder={clientName ? `${clientName} <> Obly` : 'Nome del gruppo WhatsApp'}
            className={`${inputCls} ${nameError ? '!border-[#EB5757]' : ''}`}
          />
        </div>
      )}

      {mode === 'existing' && (
        <div>
          <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">
            Scegli il gruppo <span className="text-[#EB5757]">*</span>
          </label>
          <GroupPicker clientId={clientId} value={reuseJid} onChange={onReuseJid} hideCreateNew
            className={pickError ? 'rounded-md ring-1 ring-[#EB5757]' : ''} />
          {pickError && <p className="mt-1 text-[11px] text-[#EB5757]">Seleziona un gruppo esistente.</p>}
          {reuseJid && onSendIntro && <ReuseIntroChoice value={sendIntro} onChange={onSendIntro} />}
        </div>
      )}

      {/* Contatti del cliente */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF]">
            Partecipanti dai contatti del cliente
            {mode === 'new' && <span className="text-[#EB5757]"> *</span>}
            {mode === 'existing' && <span className="text-[11px] font-normal text-[#9B9A97]"> (facoltativo)</span>}
          </label>
          <button type="button" onClick={() => setShowAdd((v) => !v)}
            className="inline-flex items-center gap-1 text-[12px] text-[#2383E2] hover:text-[#1a6cba]">
            <Plus className="w-3.5 h-3.5" /> Nuovo contatto
          </button>
        </div>

        {withContacts.length > 3 && (
          <div className="relative mb-1.5">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9B9A97]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca contatto…"
              className={`${inputCls} pl-8`} />
          </div>
        )}

        <div className={`max-h-44 overflow-y-auto rounded-md border ${numbersError ? 'border-[#EB5757]' : 'border-[rgba(55,53,47,0.12)] dark:border-[#454B4E]'}`}>
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-[12px] text-[#9B9A97]">
              {withContacts.length === 0 ? 'Nessun contatto con numero per questo cliente. Aggiungine uno.' : 'Nessun contatto trovato.'}
            </p>
          ) : filtered.map((c) => {
            const checked = c.phone ? selectedKeys.has(phoneKey(c.phone)) : false;
            return (
              <button type="button" key={c.id} onClick={() => toggleContact(c)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.04] border-b border-black/[0.04] dark:border-white/[0.04] last:border-0">
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#2383E2] border-[#2383E2]' : 'border-[#9B9A97]'}`}>
                  {checked && <span className="w-2 h-2 bg-white rounded-sm" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] text-[#37352F] dark:text-white truncate">{c.name}</span>
                  <span className="block text-[11px] text-[#9B9A97] truncate">{c.phone}{c.role ? ` · ${c.role}` : ''}</span>
                </span>
              </button>
            );
          })}
        </div>
        {numbersError && <p className="mt-1 text-[11px] text-[#EB5757]">Seleziona o aggiungi almeno un partecipante.</p>}
      </div>

      {showAdd && (
        <AddContactForm clientId={clientId} orgId={orgId} onAdded={onContactAdded} onCancel={() => setShowAdd(false)} />
      )}

      {/* Numeri aggiuntivi non in rubrica */}
      <div>
        <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">
          Altri numeri non in rubrica <span className="text-[11px] font-normal text-[#9B9A97]">(facoltativo)</span>
        </label>
        <ManualNumbers
          values={manualNumbers}
          onChange={(list) => {
            // Mantiene i numeri dei contatti selezionati + i manuali aggiornati.
            const contactNums = numbers.filter((n) => n.trim() !== '' && contactKeys.has(phoneKey(n)));
            onNumbersChange([...contactNums, ...list.filter((n) => n.trim() !== '')]);
          }}
        />
      </div>
    </div>
  );
}

/** Lista numeri manuali (add/remove). */
function ManualNumbers({ values, onChange }: { values: string[]; onChange: (list: string[]) => void }) {
  const list = values.length > 0 ? values : [''];
  const setAt = (i: number, v: string) => onChange(list.map((n, idx) => (idx === i ? v : n)));
  const add = () => onChange([...list, '']);
  const remove = (i: number) => onChange(list.length > 1 ? list.filter((_, idx) => idx !== i) : ['']);
  return (
    <>
      <div className="space-y-2">
        {list.map((num, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input type="tel" value={num} onChange={(e) => setAt(idx, e.target.value)} placeholder="+39 333 1234567"
              className={`${inputCls} flex-1`} />
            {list.length > 1 && (
              <button type="button" onClick={() => remove(idx)} aria-label="Rimuovi numero"
                className="p-1.5 text-[#EB5757] hover:bg-[#EB5757]/10 rounded transition-colors flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className="mt-2 flex items-center gap-1.5 text-[12px] text-[#2383E2] hover:text-[#1a6cba] transition-colors">
        <Plus className="w-3.5 h-3.5" /> Aggiungi numero
      </button>
    </>
  );
}

/** Form inline per creare un nuovo contatto cliente (persona clients + client_relationships). */
function AddContactForm({ clientId, orgId, onAdded, onCancel }: {
  clientId: string; orgId: string | null;
  onAdded: (c: ClientContact) => void; onCancel: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [area, setArea] = useState('');
  const [notes, setNotes] = useState('');
  const [showExtra, setShowExtra] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
  const canSave = fullName !== '' && phone.trim() !== '' && !!orgId;

  const save = async () => {
    if (!canSave) { setError('Nome e telefono sono obbligatori.'); return; }
    setSaving(true); setError(null);
    // Modello Contatti first-class: il referente diventa una persona clients(individual)
    // + link client_relationships('contatto'), non più una riga client_contacts.
    const [personId] = await persistClientReferenti(clientId, orgId!, [{
      name: fullName,
      phone: phone.trim(),
      email: email.trim() || null,
      role: role.trim() || null,
      area: (area || null) as any,
      is_primary: false,
      notes: notes.trim() || null,
    } as any]);
    setSaving(false);
    if (!personId) { setError('Errore nel salvataggio del contatto'); return; }
    onAdded({
      id: personId,
      name: fullName,
      phone: phone.trim() || null,
      email: email.trim() || null,
      role: role.trim() || null,
      area: area || null,
    });
  };

  return (
    <div className="rounded-md border border-[#2383E2]/30 bg-[#2383E2]/[0.04] p-3 space-y-2">
      <p className="text-[12px] font-medium text-[#37352F] dark:text-white">Nuovo contatto</p>
      <div className="grid grid-cols-2 gap-2">
        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Nome *" className={inputCls} />
        <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Cognome" className={inputCls} />
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono *" type="tel" className={inputCls} />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" className={inputCls} />
      </div>

      <button type="button" onClick={() => setShowExtra((v) => !v)}
        className="inline-flex items-center gap-1 text-[12px] text-[#6B6762] dark:text-[#9B9A97] hover:text-[#2383E2]">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showExtra ? 'rotate-180' : ''}`} /> Altri campi
      </button>
      {showExtra && (
        <div className="grid grid-cols-2 gap-2">
          <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Ruolo (es. Titolare)" className={inputCls} />
          <select value={area} onChange={(e) => setArea(e.target.value)} className={inputCls}>
            <option value="">Area…</option>
            {AREA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note" className={`${inputCls} col-span-2`} />
        </div>
      )}

      {error && <p className="text-[11px] text-[#EB5757]">{error}</p>}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-[12px] text-[#6B6762] dark:text-[#9B9A97] hover:bg-black/[0.04] rounded">
          Annulla
        </button>
        <button type="button" onClick={save} disabled={!canSave || saving}
          className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#2383E2] hover:bg-[#1a6cba] rounded disabled:opacity-50">
          {saving ? 'Salvataggio…' : 'Salva contatto'}
        </button>
      </div>
    </div>
  );
}
